/**
 * JSON-LD enriquecido para imóveis — complementa o schema existente sem remover campos.
 */

import { stripUndefinedDeep, toSchemaIsoDate } from './metaHelpers';
import { buildImageObjectSchema } from './schemaBuilders';

function buildAmenityFeatures(amenities) {
  if (!Array.isArray(amenities) || !amenities.length) return undefined;
  return amenities.map((amenity) => ({
    '@type': 'LocationFeatureSpecification',
    name: amenity,
    value: true,
  }));
}

function buildPostalAddress(schemaAddress) {
  if (!schemaAddress?.streetAddress && !schemaAddress?.addressLocality) return undefined;
  return stripUndefinedDeep({
    '@type': 'PostalAddress',
    streetAddress: schemaAddress.streetAddress,
    addressLocality: schemaAddress.addressLocality,
    addressRegion: schemaAddress.addressRegion,
    postalCode: schemaAddress.postalCode,
    addressCountry: schemaAddress.addressCountry || 'BR',
  });
}

function buildGeo(schemaGeo) {
  if (!schemaGeo?.latitude || !schemaGeo?.longitude) return undefined;
  return {
    '@type': 'GeoCoordinates',
    latitude: Number(schemaGeo.latitude),
    longitude: Number(schemaGeo.longitude),
  };
}

function buildFloorSize(schemaFloorSize, propertyArea) {
  const value = schemaFloorSize || propertyArea;
  if (!value) return undefined;
  return {
    '@type': 'QuantitativeValue',
    value: Number(value),
    unitCode: 'MTK',
    unitText: 'm²',
  };
}

function resolveNumberOfRooms(propertySeo, propertyData) {
  if (propertySeo?.schemaNumberOfRooms) return Number(propertySeo.schemaNumberOfRooms);
  const bedrooms = parseInt(propertyData?.bedrooms, 10) || 0;
  const suites = parseInt(propertyData?.suites, 10) || 0;
  const total = bedrooms + suites;
  return total > 0 ? total : undefined;
}

function resolveBroker(propertyData, brokerInfo) {
  const name = propertyData?.brokerName || brokerInfo?.name;
  if (!name) return undefined;
  return stripUndefinedDeep({
    '@type': 'RealEstateAgent',
    name,
    telephone: propertyData?.brokerPhone || brokerInfo?.telephone,
    email: propertyData?.brokerEmail || brokerInfo?.email,
    url: brokerInfo?.url,
  });
}

function buildImageEntities(propertySeo, propertyData, ogImage, baseUrl, metaTitle) {
  const urls = [];
  const push = (url) => {
    if (url && !urls.includes(url)) urls.push(url);
  };

  push(propertySeo?.schemaImage);
  push(ogImage);
  push(propertyData?.coverImage);
  if (Array.isArray(propertyData?.images)) {
    propertyData.images.forEach(push);
  }

  if (!urls.length) return undefined;

  return urls.map((url) => {
    const obj = buildImageObjectSchema(url, { title: metaTitle, seo: propertySeo }, baseUrl);
    return stripUndefinedDeep({
      '@type': 'ImageObject',
      contentUrl: url,
      url,
      name: obj?.name || metaTitle,
      description: obj?.description,
    });
  });
}

/** Espelha o fallback do servidor (functions/seo/pageSeoBuilder.js). */
const PROPERTY_SCHEMA_TYPE_BY_KEY = {
  house: 'SingleFamilyResidence',
  countryHouse: 'House',
  farm: 'House',
  apartment: 'Apartment',
  loft: 'Apartment',
};

function resolvePropertySchemaType(propertySeo, propertyData) {
  if (propertySeo?.schemaType) return propertySeo.schemaType;
  return PROPERTY_SCHEMA_TYPE_BY_KEY[propertyData?.type] || 'Product';
}

function buildFallbackAddress(propertySeo, propertyData) {
  const fromSeo = buildPostalAddress(propertySeo?.schemaAddress);
  if (fromSeo) return fromSeo;
  if (!propertyData?.city && !propertyData?.neighborhood) return undefined;
  return stripUndefinedDeep({
    '@type': 'PostalAddress',
    streetAddress: propertyData.neighborhood || undefined,
    addressLocality: propertyData.city || undefined,
    addressRegion: propertyData.state || undefined,
    addressCountry: 'BR',
  });
}

/**
 * Constrói JSON-LD principal do imóvel preservando todos os campos existentes e adicionando complementos.
 * Se schemaType não estiver no admin, usa fallback alinhado ao servidor.
 */
export function buildEnhancedPropertyJsonLd({
  propertySeo,
  propertyData = null,
  settings = {},
  canonicalUrl,
  metaTitle,
  metaDescription,
  ogImage,
  baseUrl,
  brokerInfo = null,
}) {
  if (!propertySeo?.schemaType && !propertyData) return null;

  const schemaType = resolvePropertySchemaType(propertySeo, propertyData);
  const seo = propertySeo || {};
  const price =
    seo.schemaPrice ||
    propertyData?.price ||
    propertyData?.salePrice ||
    propertyData?.rentPrice ||
    propertyData?.rent ||
    '0';

  const offers = stripUndefinedDeep({
    '@type': 'Offer',
    price: String(price),
    priceCurrency: seo.schemaCurrency || 'BRL',
    availability: `https://schema.org/${seo.schemaAvailability || 'InStock'}`,
    url: canonicalUrl,
    seller: settings?.companyName
      ? stripUndefinedDeep({
          '@type': 'Organization',
          name: settings.companyName,
          url: baseUrl,
          logo: settings.logo,
          telephone: settings.contactPhone,
          email: settings.contactEmail,
        })
      : undefined,
  });

  const imageEntities = buildImageEntities(
    seo,
    propertyData,
    ogImage,
    baseUrl,
    metaTitle
  );

  return stripUndefinedDeep({
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': canonicalUrl,
    name: seo.schemaName || metaTitle,
    description: seo.schemaDescription || metaDescription,
    url: canonicalUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    datePublished: toSchemaIsoDate(propertyData?.createdAt),
    dateModified: toSchemaIsoDate(propertyData?.updatedAt || propertyData?.createdAt),
    image: imageEntities || seo.schemaImage || ogImage,
    offers,
    address: buildFallbackAddress(seo, propertyData),
    geo: buildGeo(seo.schemaGeo),
    floorSize: buildFloorSize(seo.schemaFloorSize, propertyData?.area),
    numberOfRooms: resolveNumberOfRooms(seo, propertyData),
    numberOfBedrooms: propertyData?.bedrooms ? parseInt(propertyData.bedrooms, 10) : undefined,
    numberOfBathroomsTotal: propertyData?.bathrooms
      ? parseInt(propertyData.bathrooms, 10)
      : undefined,
    yearBuilt: seo.schemaYearBuilt
      ? parseInt(seo.schemaYearBuilt, 10)
      : undefined,
    amenityFeature: buildAmenityFeatures(seo.schemaAmenities),
    broker: resolveBroker(propertyData, brokerInfo),
  });
}
